import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Search,
  Filter,
  Calendar,
  Tag,
  Plus,
  RefreshCw,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'expired', label: 'Expired' },
  { value: 'refunded', label: 'Refunded' },
];

const periodOptions = [
  'weekly',
  'monthly',
  'quarterly',
  'semiannual',
  'annual',
  'lifetime',
  'custom',
];

const defaultFormState = {
  userId: '',
  planId: '',
  planName: '',
  status: 'active',
  billingPeriod: 'monthly',
  amount: '',
  currency: 'USD',
  periodStart: '',
  periodEnd: '',
  nextRenewalAt: '',
  paymentProvider: '',
  paymentReference: '',
  notes: '',
};

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [formState, setFormState] = useState(defaultFormState);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/subscriptions', {
        params: {
          page,
          limit: 20,
          search: search || undefined,
          status: status || undefined,
          planId: planFilter || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      });
      if (response.data.success) {
        setSubscriptions(response.data.subscriptions);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load subscriptions'));
    } finally {
      setLoading(false);
    }
  }, [page, search, status, planFilter, startDate, endDate]);

  useEffect(() => {
    fetchPlans();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const getErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.code === 'ERR_NETWORK') {
      return 'Unable to reach the server. Please check your network.';
    }
    return fallbackMessage;
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/admin/plans');
      if (response.data.success) {
        setPlans(response.data.plans || []);
      }
    } catch (error) {
      toast.error('Failed to load plans list');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      // Fetch all active users with a high limit to get all users
      const response = await api.get('/admin/users', {
        params: { page: 1, limit: 1000, search: '' },
      });
      if (response.data.success) {
        // Filter only active users
        const activeUsers = (response.data.users || []).filter(user => user.is_active);
        setUsers(activeUsers);
      }
    } catch (error) {
      toast.error('Failed to load users list');
    } finally {
      setLoadingUsers(false);
    }
  };

  const openCreateModal = () => {
    setSelectedSubscription(null);
    setFormState(defaultFormState);
    setShowFormModal(true);
  };

  const handleChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSubscription = async () => {
    if (!formState.userId || !formState.planName || !formState.periodStart || !formState.periodEnd) {
      toast.error('Please fill in all required fields.');
      return;
    }

    // Validate userId is selected
    if (!formState.userId) {
      toast.error('Please select a user.');
      return;
    }

    const userIdNum = Number(formState.userId);
    if (isNaN(userIdNum) || userIdNum <= 0) {
      toast.error('Please select a valid user.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        userId: userIdNum,
        planId: formState.planId ? Number(formState.planId) : null,
        planName: formState.planName,
        status: formState.status,
        billingPeriod: formState.billingPeriod,
        amount: Number(formState.amount) || 0,
        currency: formState.currency,
        periodStart: formState.periodStart,
        periodEnd: formState.periodEnd,
        nextRenewalAt: formState.nextRenewalAt || null,
        paymentProvider: formState.paymentProvider || null,
        paymentReference: formState.paymentReference || null,
        metadata: { notes: formState.notes || '' },
      };

      if (selectedSubscription) {
        await api.put(`/admin/subscriptions/${selectedSubscription.id}`, payload);
        toast.success('Subscription updated successfully');
      } else {
        await api.post('/admin/subscriptions', payload);
        toast.success('Subscription created successfully');
      }

      setShowFormModal(false);
      setFormState(defaultFormState);
      fetchSubscriptions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save subscription'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/admin/subscriptions/${deleteTarget.id}`);
      toast.success('Subscription deleted successfully');
      fetchSubscriptions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete subscription'));
    } finally {
      setDeleting(false);
      setShowConfirmModal(false);
      setDeleteTarget(null);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setStatus('');
    setPlanFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const formatCurrency = (amount, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount || 0));

  const statusBadgeClasses = useMemo(
    () => ({
      trial: 'bg-blue-100 text-blue-700',
      active: 'bg-green-100 text-green-700',
      past_due: 'bg-yellow-100 text-yellow-700',
      canceled: 'bg-gray-100 text-gray-600',
      expired: 'bg-gray-100 text-gray-600',
      refunded: 'bg-purple-100 text-purple-700',
    }),
    []
  );

  return (
    <AdminSidebar>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-purple-400">Billing</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">Subscription Management</h1>
            <p className="text-sm text-gray-600 mt-1">Track user subscription history with advanced filters.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-lg hover:from-purple-700 hover:to-pink-700"
            >
              <Plus className="h-5 w-5" />
              Add Manual Record
            </button>
            <button
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-purple-200 text-purple-700 bg-white hover:bg-purple-50"
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by email or name…"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
            </div>
            {filtersOpen && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                  <select
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan</label>
                  <select
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    value={planFilter}
                    onChange={(e) => {
                      setPlanFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All plans</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" /> Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" /> End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
              </div>
            )}
            {filtersOpen && (
              <div className="flex gap-2 justify-end">
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-purple-600 animate-spin" />
          </div>
        ) : subscriptions.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-8 w-8" />}
            title={search ? `No subscriptions for “${search}”` : 'No subscriptions yet'}
            description={
              search
                ? 'Try adjusting your filters to find what you’re looking for.'
                : 'Subscription records will appear here once users start billing.'
            }
            action={
              search
                ? {
                    label: 'Clear search',
                    onClick: resetFilters,
                  }
                : {
                    label: 'Add manual record',
                    onClick: openCreateModal,
                  }
            }
          />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="px-4 sm:px-6 py-4 text-left">User</th>
                    <th className="px-4 sm:px-6 py-4 text-left">Plan</th>
                    <th className="px-4 sm:px-6 py-4 text-left hidden lg:table-cell">Period</th>
                    <th className="px-4 sm:px-6 py-4 text-left hidden md:table-cell">Amount</th>
                    <th className="px-4 sm:px-6 py-4 text-left">Status</th>
                    {/* <th className="px-4 sm:px-6 py-4 text-right">Actions</th> */}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subscriptions.map((subscription) => (
                    <tr key={subscription.id} className="hover:bg-purple-50/40 transition">
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">
                            {subscription.user_first_name} {subscription.user_last_name}
                          </span>
                          <span className="text-xs text-gray-500">{subscription.user_email}</span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-gray-900 flex items-center gap-2">
                            <Tag className="h-4 w-4 text-purple-500" />
                            {subscription.plan_name}
                          </span>
                          <span className="text-xs text-gray-500 capitalize">
                            {subscription.billing_period.replace('_', ' ')}
                          </span>
                          {subscription.metadata?.cancellationNote?.reason === 'COMP status activated' && (
                            <div className="mt-1 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                              <span className="text-xs font-semibold text-purple-900 block mb-1">
                                COMP Status Activation
                              </span>
                              <span className="text-xs text-purple-700 block">
                                Canceled by: {subscription.metadata.cancellationNote.adminName}
                              </span>
                              <span className="text-xs text-purple-600 block">
                                {subscription.metadata.cancellationNote.adminEmail}
                              </span>
                              {subscription.metadata.cancellationNote.canceledAt && (
                                <span className="text-xs text-purple-500 block mt-1">
                                  {new Date(subscription.metadata.cancellationNote.canceledAt).toLocaleString()}
                                </span>
                              )}
                            </div>
                          )}
                          {subscription.metadata?.notes && (
                            <div className="text-xs text-gray-500 mt-1">
                              {Array.isArray(subscription.metadata.notes) ? (
                                subscription.metadata.notes.map((note, idx) => (
                                  <span key={idx} className="block text-gray-400 truncate">
                                    {note}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-400 truncate">{subscription.metadata.notes}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                        <div className="text-sm text-gray-700">
                          {new Date(subscription.period_start).toLocaleDateString()} —{' '}
                          {new Date(subscription.period_end).toLocaleDateString()}
                        </div>
                        {subscription.next_renewal_at && (
                          <div className="text-xs text-gray-500">
                            Next: {new Date(subscription.next_renewal_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                        <div className="inline-flex items-center gap-1 font-semibold text-gray-900">
                          
                          {formatCurrency(subscription.amount, subscription.currency)}
                        </div>
                        <div className="text-xs text-gray-500 uppercase">{subscription.currency}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            statusBadgeClasses[subscription.status] || 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current mr-1.5" />
                          {subscription.status.replace('_', ' ')}
                        </span>
                      </td>
                      {/* <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <select
                            className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-600"
                            value={subscription.status}
                            onChange={(e) => handleStatusUpdate(subscription, e.target.value)}
                          >
                            {statusOptions
                              .filter((option) => option.value)
                              .map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                          </select>
                          <button
                            onClick={() => openEditModal(subscription)}
                            className="p-2 rounded-lg text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm(subscription)}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-4 sm:px-6 py-4">
                <p className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.pages))}
                    disabled={page === pagination.pages}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Add/Edit Subscription Modal */}
      {showFormModal && (
        <Modal
          title={selectedSubscription ? 'Edit Subscription' : 'Add Subscription'}
          onClose={() => {
            setShowFormModal(false);
            setFormState(defaultFormState);
            setSelectedSubscription(null);
          }}
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  User <span className="text-red-500">*</span>
                </label>
                <select
                  value={formState.userId}
                  onChange={(e) => handleChange('userId', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                  disabled={loadingUsers}
                >
                  <option value="">Select a user</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email}) - ID: {user.id}
                    </option>
                  ))}
                </select>
                {loadingUsers && (
                  <p className="mt-1 text-xs text-gray-500">Loading users...</p>
                )}
                {!loadingUsers && users.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No active users found</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Linked Plan</label>
                <select
                  value={formState.planId}
                  onChange={(e) => handleChange('planId', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                >
                  <option value="">Manual entry</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formState.planName}
                onChange={(e) => handleChange('planName', e.target.value)}
                className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                placeholder="Plan name as shown to users"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formState.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none capitalize"
                >
                  {statusOptions
                    .filter((option) => option.value)
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Billing Period</label>
                <select
                  value={formState.billingPeriod}
                  onChange={(e) => handleChange('billingPeriod', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none capitalize"
                >
                  {periodOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={formState.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Currency</label>
                <input
                  type="text"
                  value={formState.currency}
                  onChange={(e) => handleChange('currency', e.target.value.toUpperCase())}
                  maxLength={10}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none uppercase"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Next Renewal</label>
                <input
                  type="datetime-local"
                  value={formState.nextRenewalAt}
                  onChange={(e) => handleChange('nextRenewalAt', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Period Start <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={formState.periodStart}
                  onChange={(e) => handleChange('periodStart', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Period End <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={formState.periodEnd}
                  onChange={(e) => handleChange('periodEnd', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Payment Provider</label>
                <input
                  type="text"
                  value={formState.paymentProvider}
                  onChange={(e) => handleChange('paymentProvider', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                  placeholder="Stripe, PayPal, etc."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Payment Reference</label>
                <input
                  type="text"
                  value={formState.paymentReference}
                  onChange={(e) => handleChange('paymentReference', e.target.value)}
                  className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                  placeholder="Transaction ID"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={formState.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="mt-1 w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-purple-500 focus:outline-none"
                rows="3"
                placeholder="Internal notes about this subscription"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setShowFormModal(false);
                  setFormState(defaultFormState);
                  setSelectedSubscription(null);
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSubscription}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : selectedSubscription ? 'Save Changes' : 'Create Subscription'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showConfirmModal && deleteTarget && (
        <ConfirmDialog
          title="Delete Subscription"
          description={`Are you sure you want to delete the subscription for ${deleteTarget.user_email}? This action cannot be undone.`}
          confirmLabel={deleting ? 'Deleting…' : 'Yes, delete'}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowConfirmModal(false);
            setDeleteTarget(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminSubscriptions;

